import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  BackendPushErrorResponse,
  Datastore,
  Invoice,
  MilestoneDraft,
  ProjectViewUser,
} from '@freelancer/datastore';
import { PaymentsCart } from '@freelancer/payments-cart';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType, HeadingWeight } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { toNumber } from '@freelancer/utils';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { ContextTypeApi, DestinationApi } from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

interface InvoiceAndFreelancer {
  invoice: Invoice;
  freelancer: ProjectViewUser;
}

@Component({
  template: `
    <ng-container
      *ngIf="
        invoiceAndFreelancer$ | async as invoiceAndFreelancer;
        else Loading
      "
    >
      <fl-bit
        class="ModalBody"
        flTrackingSection="CreateMilestoneFromInvoiceModal"
      >
        <fl-heading
          i18n="Create milestone modal heading"
          [size]="TextSize.LARGE"
          [weight]="HeadingWeight.BOLD"
          [headingType]="HeadingType.H1"
          [flMarginBottom]="Margin.SMALL"
        >
          Confirm Payment
        </fl-heading>
        <fl-text
          i18n="Create milestone modal body"
          [flMarginBottom]="Margin.LARGE"
        >
          This action will immediately fund a Milestone Payment, which will be
          released to
          <fl-text [weight]="FontWeight.BOLD" [fontType]="FontType.SPAN">
            {{ invoiceAndFreelancer.freelancer.displayName }}
          </fl-text>
          on Wednesday.
        </fl-text>
        <fl-banner-alert
          *ngIf="backendResponse"
          [type]="BannerAlertType.ERROR"
          [closeable]="false"
          [flMarginBottom]="Margin.SMALL"
          [ngSwitch]="backendResponse?.errorCode"
        >
          <ng-container
            *ngSwitchCase="ErrorCodeApi.INVOICE_HAS_LINKED_MILESTONE"
            i18n="Failed to create milestone for invoices error message as a milestone exists"
          >
            A Milestone Payment has already been created to fund these tracked
            hours. Please refresh the page.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.INVOICE_NOT_UNPAID"
            i18n="Failed to create milestone for not unpaid invoices error message"
          >
            You are not required to Pay for these tracked hours at this time.
            Please refresh the page.
          </ng-container>
          <ng-container
            *ngSwitchDefault
            i18n="Failed to create milestone for invoices error message"
          >
            Failed to create milestone payment. Please try again or contact
            support@freelancer.com with request id:
            {{ backendResponse.requestId }}
          </ng-container>
        </fl-banner-alert>
        <fl-bit class="InvoiceInfoTable" [flMarginBottom]="Margin.LARGE">
          <fl-text i18n="Invoice date range" [flMarginBottom]="Margin.MID">
            {{
              invoiceAndFreelancer.invoice.periodStart
                | dateRange: invoiceAndFreelancer.invoice.periodEnd
            }}
          </fl-text>
          <fl-list [bottomBorder]="true">
            <fl-list-item>
              <fl-bit class="Row">
                <fl-text i18n="Label for tracked hours" [color]="FontColor.MID">
                  Tracked Hours
                </fl-text>
                <fl-text i18n="Tracked hours number">
                  {{ invoiceAndFreelancer.invoice.totalHours }} hours
                </fl-text>
              </fl-bit>
            </fl-list-item>
            <fl-list-item>
              <fl-bit class="Row">
                <fl-text i18n="Label for amount to pay" [color]="FontColor.MID">
                  Amount to Pay
                </fl-text>
                <fl-text [weight]="FontWeight.BOLD">
                  {{
                    invoiceAndFreelancer.invoice.totalHoursAmount
                      | flCurrency: invoiceAndFreelancer.invoice.currency.code
                  }}
                </fl-text>
              </fl-bit>
            </fl-list-item>
          </fl-list>
        </fl-bit>
        <fl-button
          class="ConfirmButton"
          i18n="Confirm and pay button"
          flTrackingLabel="CreateMilestoneButton"
          [color]="ButtonColor.SECONDARY"
          [busy]="busy"
          (click)="handleConfirmClick(invoiceAndFreelancer.invoice)"
        >
          Confirm and Pay
        </fl-button>
      </fl-bit>
    </ng-container>
    <ng-template #Loading>
      <fl-bit class="LoadingState">
        <fl-spinner
          flTrackingLabel="CreateMilestoneFromInvoiceModalInitialisationSpinner"
          [overlay]="true"
        ></fl-spinner>
      </fl-bit>
    </ng-template>
  `,
  styleUrls: ['./create-milestone-from-invoice-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateMilestoneFromInvoiceModalComponent implements OnInit {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  ErrorCodeApi = ErrorCodeApi;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  TextSize = TextSize;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  Margin = Margin;

  @Input() invoiceId: number;
  invoice$: Rx.Observable<Invoice>;
  busy = false;
  backendResponse:
    | BackendPushErrorResponse<'carts'>
    | BackendPushErrorResponse<'milestoneDrafts'>
    | undefined = undefined;
  invoiceAndFreelancer$: Rx.Observable<InvoiceAndFreelancer>;

  constructor(
    private cart: PaymentsCart,
    private datastore: Datastore,
    private modalRef: ModalRef<CreateMilestoneFromInvoiceModalComponent>,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.invoice$ = this.datastore
      .document('invoices', this.invoiceId)
      .valueChanges();

    const freelancer$ = this.datastore
      .document(
        'projectViewUsers',
        this.invoice$.pipe(map(invoice => toNumber(invoice.freelancerId))),
      )
      .valueChanges();

    this.invoiceAndFreelancer$ = Rx.combineLatest([
      this.invoice$,
      freelancer$,
    ]).pipe(
      map(([invoice, freelancer]) => ({
        invoice,
        freelancer,
      })),
    );
  }

  handleConfirmClick(invoice: Invoice) {
    this.busy = true;
    this.backendResponse = undefined;
    const milestoneDraft = this.constructMilestoneDraft(invoice);
    this.datastore
      .createObject('milestoneDrafts', milestoneDraft)
      .then(response => {
        if (response.status === 'error') {
          throw response;
        } else {
          const milestoneDraftId = response.id;
          return this.cart.handle(
            `milestone for invoice ${invoice.id}`,
            {
              destination: DestinationApi.PROJECT_VIEW_PAGE,
              payload: `${invoice.projectId}`,
            },
            [this.constructCartItem(invoice, milestoneDraftId)],
          );
        }
      })
      .then(response => {
        if (response.status === 'success') {
          this.modalRef.close();
        } else {
          throw response;
        }
      })
      .catch(response => {
        this.busy = false;
        this.backendResponse = response;
        this.changeDetector.detectChanges();
      });
  }

  constructMilestoneDraft(invoice: Invoice): MilestoneDraft {
    if (!invoice.bidId) {
      throw new Error('Bid id is missing from the invoice.');
    }
    if (!invoice.currency) {
      throw new Error('Currency is missing from the invoice.');
    }

    return {
      amount: invoice.totalHoursAmount,
      bidderId: invoice.freelancerId,
      bidId: invoice.bidId,
      currencyId: invoice.currency.id,
      description: `Milestone for invoice ${invoice.id}`,
      id: 110, // dummy id
      projectId: invoice.projectId,
      projectOwnerId: invoice.employerId,
      timeCreated: Date.now(),
      invoiceId: invoice.id,
    };
  }

  constructCartItem(invoice: Invoice, milestoneDraftId: number) {
    if (!invoice.currency) {
      throw new Error('Currency is missing from the invoice.');
    }

    return {
      contextType: ContextTypeApi.MILESTONE,
      contextId: `${milestoneDraftId}`,
      description: `Milestone for invoice ${invoice.id}`,
      currencyId: invoice.currency.id,
      amount: invoice.totalHoursAmount,
      useBonus: true,
    };
  }
}
